import { useState, useEffect } from "react";
import type { User } from "@/mocks/users";
import { mockUsers } from "@/mocks/users";
import { departmentsApi, type Department, ApiError } from "@/lib/api";
import UsersTab from "./components/UsersTab";
import DepartmentsTab from "./components/DepartmentsTab";
import UserModal from "./components/UserModal";

type Tab = "users" | "departments";

export default function DepartmentsUsers() {
  const [activeTab, setActiveTab] = useState<Tab>("users");

  // Users remain local (no users API yet)
  const [users, setUsers] = useState<User[]>(mockUsers);
  const [modalMode, setModalMode] = useState<"add" | "edit" | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Departments from API
  const [departments, setDepartments] = useState<Department[]>([]);
  const [deptLoading, setDeptLoading] = useState(true);
  const [deptError, setDeptError] = useState<string | null>(null);

  useEffect(() => {
    departmentsApi.list(1)
      .then((res) => setDepartments(res.data.data))
      .catch((err) => setDeptError(err instanceof ApiError ? err.message : "Failed to load departments"))
      .finally(() => setDeptLoading(false));
  }, []);

  const handleAdd = () => { setEditingUser(null); setModalMode("add"); };
  const handleEdit = (user: User) => { setEditingUser(user); setModalMode("edit"); };

  const handleSave = (user: User) => {
    if (modalMode === "add") {
      setUsers((prev) => [...prev, user]);
    } else {
      setUsers((prev) => prev.map((u) => (u.id === user.id ? user : u)));
    }
    setModalMode(null);
    setEditingUser(null);
  };

  const handleToggleStatus = (userId: string) => {
    setUsers((prev) =>
      prev.map((u) => u.id === userId ? { ...u, status: u.status === "Active" ? "Inactive" : "Active" } : u)
    );
  };

  const handleDelete = (userId: string) => {
    setUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const handleAddDepartment = async (name: string) => {
    try {
      const res = await departmentsApi.create({ name, is_active: true });
      setDepartments((prev) => [...prev, res.data]);
    } catch (err) {
      if (err instanceof ApiError) setDeptError(err.message);
    }
  };

  const handleDeleteDepartment = async (id: number) => {
    try {
      await departmentsApi.delete(id);
      setDepartments((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      if (err instanceof ApiError) setDeptError(err.message);
    }
  };

  const deptNames = departments.map((d) => d.name);

  const stats = {
    total: users.length,
    active: users.filter((u) => u.status === "Active").length,
    admins: users.filter((u) => u.role === "System Admin").length,
    departments: departments.length,
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-primary-600 text-xl md:text-2xl font-bold">Departments & Users</h1>
          <p className="text-foreground-500 text-sm mt-1">Manage users, departments, and role-based privileges</p>
        </div>
        {activeTab === "users" && (
          <button
            onClick={handleAdd}
            className="h-10 px-4 bg-primary-600 text-white text-sm font-semibold rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-user-add-line text-sm"></i>
            <span>Add User</span>
          </button>
        )}
      </div>

      {deptError && (
        <div className="bg-accent-50 border border-accent-200 rounded-md px-4 py-2.5 flex items-center gap-2 mb-4">
          <i className="ri-error-warning-line text-accent-600 text-sm"></i>
          <p className="text-accent-700 text-sm">{deptError}</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-primary-50 flex items-center justify-center">
              <i className="ri-team-line text-primary-600 text-sm"></i>
            </div>
            <p className="text-foreground-500 text-xs font-medium">Total Users</p>
          </div>
          <p className="text-foreground-800 text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-secondary-50 flex items-center justify-center">
              <i className="ri-user-follow-line text-secondary-600 text-sm"></i>
            </div>
            <p className="text-foreground-500 text-xs font-medium">Active</p>
          </div>
          <p className="text-foreground-800 text-2xl font-bold">{stats.active}</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-accent-50 flex items-center justify-center">
              <i className="ri-shield-user-line text-accent-600 text-sm"></i>
            </div>
            <p className="text-foreground-500 text-xs font-medium">Admins</p>
          </div>
          <p className="text-foreground-800 text-2xl font-bold">{stats.admins}</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-background-200 flex items-center justify-center">
              <i className="ri-building-2-line text-foreground-500 text-sm"></i>
            </div>
            <p className="text-foreground-500 text-xs font-medium">Departments</p>
          </div>
          <p className="text-foreground-800 text-2xl font-bold">
            {deptLoading ? <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i> : stats.departments}
          </p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="inline-flex bg-background-200 rounded-full px-1 py-1 mb-5">
        <button
          onClick={() => setActiveTab("users")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${activeTab === "users" ? "bg-white text-foreground-800" : "text-foreground-500 hover:text-foreground-700"}`}
        >
          <i className="ri-team-line mr-1.5"></i>Users
        </button>
        <button
          onClick={() => setActiveTab("departments")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${activeTab === "departments" ? "bg-white text-foreground-800" : "text-foreground-500 hover:text-foreground-700"}`}
        >
          <i className="ri-building-2-line mr-1.5"></i>Departments
        </button>
      </div>

      {activeTab === "users" ? (
        <UsersTab users={users} onEdit={handleEdit} onAdd={handleAdd} onToggleStatus={handleToggleStatus} onDelete={handleDelete} />
      ) : (
        <DepartmentsTab
          users={users}
          departments={departments}
          deptNames={deptNames}
          loading={deptLoading}
          onAddDepartment={handleAddDepartment}
          onDeleteDepartment={handleDeleteDepartment}
        />
      )}

      {modalMode && (
        <UserModal
          mode={modalMode}
          user={editingUser}
          onClose={() => { setModalMode(null); setEditingUser(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
